import { ol, ul } from './helpers';
import { GLOBAL_FIELD_NAMES, STRENGTH_WEAKNESSES_NAMES } from '../Sections/Global/form';

const COMPANY_STRENGTHS_INSTRUCTIONS = {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: {
    name: 'Strengths',
    instruction:
      'Identify core competencies and resources giving market edge. Highlight specialized skills, unique capabilities, and brand reputation. ',
    exampleOne: ul([
      'Specialized expertise in designing and manufacturing tracked tractors for heavy or wet soils.',
      'Unique patented rubber-covered continuous track technology providing superior traction and flotation.',
      'The strong team comprising experienced engineers, agricultural specialists, and industry professionals.',
    ]),
    exampleTwo: ul([
      'A secure payment system ensures that financial transactions are conducted safely within the platform.',
      'The rigorous vetting process for translators ensures high-quality and reliable services.',
      'Dedicated team with expertise in programming, language services, and customer support.',
    ]),
  },
};

const COMPANY_WEAKNESSES_INSTRUCTIONS = {
  [STRENGTH_WEAKNESSES_NAMES.TITLE]: {
    name: 'Weaknesses',
    instruction:
      'Identify company`s internal limitations and challenges. Transparently discuss areas needing improvement: processes, resources, skills. Address gaps in meeting industry benchmarks, operational inefficiencies, and their impact on performance and competitiveness.',
    exampleOne: ul([
      'Reliance on a single patented technology for tractor differentiation limits product diversification.',
      'Challenges in scaling production to meet increasing demand due to constraints in manufacturing capacity.',
      'Lack of geographic diversification, with a heavy reliance on a specific market or region for sales.',
    ]),
    exampleTwo: ul([
      'Limited brand awareness and market presence compared to established competitors.',
      'Dependency on third-party technology providers for certain platform functionalities.',
      'Dependence on a relatively small team may lead to resource constraints during periods of high demand.',
    ]),
  },
};

export const GLOBAL_INSTRUCTIONS = {
  [GLOBAL_FIELD_NAMES.COMPANY_NAME]: {
    name: 'Company Name',
    instruction:
      'Provide the intended full and official name of your company. If the company is not yet registered, ensure the name reflects your brand vision and planned official registration.',
    exampleOne: 'Agrotech',
    exampleTwo: 'LanguageLab',
  },
  [GLOBAL_FIELD_NAMES.REGISTRATION_DATE]: {
    name: 'Registration Date',
    instruction:
      'State the precise date when your company was officially registered, include the year, month, and day of registration.',
    exampleOne: '8.16.2001',
    exampleTwo: '3.15.2008',
  },
  [GLOBAL_FIELD_NAMES.ADDRESS]: {
    name: 'Address of Registration',
    instruction:
      'Specify the city and country where your company is officially registered. Provide accurate details to facilitate legal inquiries or business transactions.',
    exampleOne: '89 N Smeeth St, Charlotte, CA 28202, US',
    exampleTwo: '100 N Tailor St, Charlotte, NC 28202, US',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION]: {
    name: 'Company Brief',
    instruction:
      'Specify a concise company overview. What unique aspects differentiate your business in the market? Detail key products or services, highlighting innovation or competitive advantages. Mention significant milestones and any strategic partnerships.',
    exampleOne:
      'Agrotech manufactures powerful tracked tractors for heavy or wet soils. Its unique continuous track design offers superior traction and flotation, minimizing soil pressure and impact with patented rubber. Prioritizing performance and environmental responsibility, the company provides cutting-edge solutions for modern farming challenges.',
    exampleTwo:
      'LanguageLab is a language services platform streamlining global communication with over 100 languages. The Company empowers clients and translators to optimize their communication experience by facilitating seamless interaction processes.',
  },
  [GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES]: {
    name: 'Number of Employees',
    instruction:
      "Provide an accurate count of your company's current employees, including full-time, part-time, and contract workers",
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.ANNUAL_INCOME]: {
    name: 'Past Annual Income',
    instruction:
      'Calculate and provide the average annual income by summing your total income for the past three years and dividing by three. This figure should be accurate and reflect your financial performance consistently over the period.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION]: {
    name: 'Description of the Project',
    instruction:
      'Succinctly describe investment project essence, be it new facility, modernization, or market expansion. Briefly introduce target business, its industry, market presence, core products, financial health, and strengths, outlining acquisition rationale.',
    exampleOne:
      'The business plan focuses on specialized tracked tractors with patented continuous track technology, combating low productivity in heavy soils. Leveraging innovation, tractors offer longer lifespans, reducing costs and boosting profitability for farmers in the long run.',
    exampleTwo:
      'The Company addresses translation market decentralization with a centralized platform, robust vetting, secure payments, and AI-driven technology. Our strategy leverages tech to streamline processes, enhancing communication, efficiency, and trust for stakeholders.',
  },
  [GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES]: {
    name: 'Main Products/Services Offered',
    instruction:
      'Present a structured list of your products or services. Group them into categories or highlight any flagship offerings that define your business. Emphasize the essential characteristics or qualities that set your products or services apart from competitors, such as innovation, quality, or unique features.',
    exampleOne:
      'The company specializes in manufacturing powerful tracked tractors engineered for heavy or wet soils. Products include:' +
      ol(['Tracked Tractors', 'Farming Equipment', 'Environmental Solutions']),
    exampleTwo:
      'The company offers a range of products and services designed to streamline global communication and optimize interaction processes. The offerings include:' +
      ol([
        'Translation Services',
        'AI-Powered Translation Tools',
        'Communication Optimization Solutions',
        'Innovative Technology Solutions',
      ]),
  },
  [GLOBAL_FIELD_NAMES.COMPANY_SECTOR]: {
    name: 'Operating Sector',
    instruction:
      'Specify the sector your company operates in, selecting the appropriate level of detail from the provided dropdown options.',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_SUB_SECTOR]: {
    name: 'Operating Subsector',
    instruction:
      'Specify the subsector your company operates in, selecting the appropriate level of detail from the provided dropdown options.',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY_GROUP]: {
    name: 'Operating Industry Group',
    instruction:
      'Specify the industry group your company operates in, selecting the appropriate level of detail from the provided dropdown options.',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_INDUSTRY]: {
    name: 'Operating Industry',
    instruction:
      'Specify the industry your company operates in, selecting the appropriate level of detail from the provided dropdown options.',
    exampleOne: 'Farm Machinery and Equipment Manufacturing.',
    exampleTwo: 'Translation and Interpretation Services.\n Web Search Portals and All Other Information Services.',
  },
  [GLOBAL_FIELD_NAMES.MAIN_GOALS]: {
    name: 'Main Goals and Objectives',
    instruction:
      'Outline company strategic goals, emphasizing short, medium, long-term objectives aligned with mission and vision. Specify focus areas like market expansion, revenue growth, or product innovation. Discuss strategies, initiatives, KPIs driving goal realization.',
    exampleOne:
      'Long-term: Achieve global market leadership by diversifying product offerings, investing in research and development for sustainable farming technologies, and expanding into emerging markets. KPIs include market dominance and customer satisfaction levels.',
    exampleTwo:
      'Short-term: Enhance platform usability with new features, increase user base through targeted marketing campaigns. KPI: User engagement metrics.',
  },
  [GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS]: {
    name: 'Strategy for Project Success',
    instruction:
      'Define success strategy pillars: customer focus, innovation, operational excellence. Detail tactics like market differentiation, innovation, efficiency, customer engagement, and adaptation to challenges. Emphasize continuous improvement and adaptability.',
    exampleOne:
      'The company`s strategy for achieving success in this project revolves around three key pillars: innovation, market differentiation, and customer-centricity. The company continuously invests in research and development to enhance product offerings and stay ahead of market trends.',
    exampleTwo:
      'Company`s strategy focuses on innovation, customer-centricity, and agility. By continuously refining products based on market feedback, prioritizing customer satisfaction, and adapting swiftly to industry trends, the company aims to drive sustainable growth and establish ourselves as leaders in the language services sector.',
  },
  [GLOBAL_FIELD_NAMES.TARGET_MARKET]: {
    name: 'Target Market and Consumption ',
    instruction:
      'Provide concise market overview: needs, preferences, pain points addressed. Highlight unique aspects like geographic concentration. Estimate market size citing sources. Emphasize economic value and business opportunities.',
    exampleOne:
      'The company`s target market for tracked tractors comprises various agricultural entities, including small-scale family farms, mid-sized operations, and large commercial enterprises across different geographic regions. The annual consumption within the target market is $100 mln.',
    exampleTwo:
      'The company`s target market comprises businesses and individuals with global communication needs, including multinational corporations, international organizations, language service providers, and individuals seeking translation services. The annual consumption size is $49.6 billion',
  },
  [GLOBAL_FIELD_NAMES.TARGET_AUDIENCE]: {
    name: 'Target Audience',
    instruction:
      'Define target audience: demographics, psychographics, needs. Emphasize product/service fit. Highlight unique characteristics: geography, industry, behavior.',
    exampleOne:
      'The target audience encompasses various stakeholders within the agricultural sector, including farmers, agricultural cooperatives, agribusinesses, and rural enterprises. These entities represent the backbone of global food production, spanning various agricultural segments such as row crops, speciality crops, livestock, and horticulture.',
    exampleTwo:
      'The target audience includes businesses, organizations, and individuals with diverse language needs. Main customer types comprise multinational corporations, government agencies, international NGOs, language service providers, educational institutions, and individuals seeking translation services for personal or professional use.',
  },
  [GLOBAL_FIELD_NAMES.TARGET_CHANNELS]: {
    name: 'Sales Channels',
    instruction:
      'Overview primary distribution channels: direct sales, retail, e-commerce. Discuss rationale, considering reach, preferences, industry standards. Address channel-specific advantages and challenges.',
    exampleOne: ol([
      'Dealer and Distributor Network: The company partners with dealerships and distributors worldwide to sell its tracked tractors.',
      'Direct Sales: The company maintains a direct sales team responsible for engaging with customers, providing product information, and facilitating sales transactions.',
    ]),
    exampleTwo:
      'The company sells its services through various channels, including the online platform, direct sales teams, partnerships with language service providers, affiliate programs, and participation in industry events and conferences. Additionally, the company utilizes digital marketing strategies such as social media, email campaigns.',
  },
  [GLOBAL_FIELD_NAMES.MAIN_COMPETITORS]: {
    name: 'Main Competitors',
    instruction:
      'Analyze competitive landscape: main competitors, market share, offerings, reach. Discuss distinctive characteristics or advantages.',
    exampleOne:
      'The company`s main competitors in the agricultural machinery market include global giants such as Company X and Company Y and regional players like Company Z. They offer a range of tractors and farm equipment with advanced features and technology, competing directly with the company`s tracked tractors in terms of performance and innovation.',
    exampleTwo:
      'The company`s main competitors include established language service providers such as [Competitor A], [Competitor B], and [Competitor C], as well as emerging players in the language technology sector like [Competitor D] and [Competitor E]. These companies offer similar translation services and AI-powered solutions targeting global communication needs.',
  },
  [GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME]: {
    name: 'Projected Annual Income',
    instruction:
      'Calculate the projected average annual income by summing the expected annual incomes for the next three years and then dividing by three. Use the formula:\n\nAverage Projected Annual Income =\n(Income Year 1 + Income Year 2 + Income Year 3) / 3\n\nProvide the projected income for each year and justify your assumptions based on market trends, sales forecasts, and other relevant factors.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY]: {
    name: 'Projected Profitability',
    instruction:
      'To calculate forecasted profitability in percentage:' +
      ol([
        'Estimate future net profit.',
        'Forecast total revenue.',
        'Divide net profit by total revenue.',
        'Multiply the result by 100 to convert to a percentage.',
      ]) +
      'Utilize the formula: (Net Profit / Total Revenue) * 100 = Forecasted Profitability (%).',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD]: {
    name: 'Projected Payback Period',
    instruction:
      'To calculate the projected payback period on profit:' +
      ol([
        'Determine the total initial investment.',
        'Forecast annual net profit.',
        'Divide the total initial investment by the annual net profit.',
        'Convert the result to months by multiplying by 12.',
      ]) +
      'Utilize the formula: Payback Period =\n(Total Initial Investment / Annual Net Profit) * 12',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.TOTAL_COST]: {
    name: 'Total Project Cost',
    instruction:
      'Conduct a meticulous assessment of the projected expenditures. The comprehensive budgetary analysis should encompass diverse facets such as operational costs, capital expenditures, marketing initiatives, technological investments, and workforce allocations.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.FUNDING_AMOUNT]: {
    name: 'Funding Needs',
    instruction:
      'Determine the required funding by conducting a comprehensive assessment of all project expenses and anticipated costs.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING]: {
    name: 'Funding by Own Funds',
    instruction: 'Specify the percentage of project cost to be financed internally.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.MISSION]: {
    name: 'Mission Statement',
    instruction:
      'Define company mission, stating purpose and values. Highlight impact on marketplace or community, emphasizing unique value proposition. Align mission with broader goals, express commitment to principles like innovation, customer service, or sustainability.',
    exampleOne:
      'The company`s mission is to develop advanced tractor solutions rooted in sustainability and technological innovation, serving the global agricultural community.',
    exampleTwo:
      'The company`s mission is to break down language barriers and foster global connections by providing seamless, high-quality translation services through innovative technology and exceptional customer service.',
  },
  [GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS]: COMPANY_STRENGTHS_INSTRUCTIONS,
  [GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES]: COMPANY_WEAKNESSES_INSTRUCTIONS,
  [GLOBAL_FIELD_NAMES.COMPETITIVE_ADVANTAGE]: {
    name: 'Unique Features or Competitive Advantage',
    instruction:
      'Begin with a hook or a captivating statement to grab the reader"s attention. Clearly state the primary purpose or function of the product. Highlight one or two key features that distinguish it from others in the market.',
    exampleOne:
      'The tracked tractors stand out due to their innovative continuous track design, which sets them apart from conventional wheeled tractors. This design offers unparalleled traction and flotation, particularly beneficial for navigating heavy or wet soils. Additionally, the patented rubber coating on the tracks minimizes soil pressure and impact, reducing crop damage and soil compaction.',
    exampleTwo: ol([
      'Proprietary Technology: the platform harnesses state-of-the-art in-house AI technology, giving a competitive edge in efficiency, accuracy, and innovation.',
      'Comprehensive Language Support: With over 100 languages, services cover various linguistic needs, offering clients unparalleled access to global communication channels.',
    ]),
  },
  [GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS]: {
    name: 'Product/Service Development Plans',
    instruction:
      'Articulate your organization`s strategic vision for product development and expansion, detailing future research initiatives and potential new product categories or sectors.',
    exampleOne:
      'The plans for new product development include the following:' +
      ol([
        'Implements and Attachments: the company is developing a range of advanced implements and attachments that can be used with tracked tractors to enhance versatility and productivity in various farming tasks such as planting, tillage, and harvesting.',
      ]),
    exampleTwo:
      'Potential areas where the language services platform will consider developing new or related products:' +
      ol([
        'Multimodal Translation Services: Developing services that support translation across multiple modalities, such as text, speech, images, and video, to provide more comprehensive language solutions.',
      ]),
  },
  [GLOBAL_FIELD_NAMES.LOAN_BORROW_AMOUNT]: {
    name: 'Loan Amount Planned',
    instruction:
      'Outline the loan amount and rationale. Justify borrowing based on cost-effectiveness, leveraging opportunities, and maintaining financial flexibility for growth and stability.',
    exampleOne: '',
    exampleTwo: '',
  },
  [GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT]: {
    name: 'Investment Raised',
    instruction:
      'Specify the investment amount and rationale. Justify the need for investment based on growth opportunities, market demand, and strategic objectives, aiming to enhance company value and competitiveness.',
    exampleOne: '',
    exampleTwo: '',
  },

  [GLOBAL_FIELD_NAMES.REVENUE_STREAMS]: {
    name: 'Business Model',
    instruction:
      'Outline primary revenue streams, such as product sales, subscriptions, or services. Summarize critical financial assumptions including pricing, growth rates, and market demand. List key partners, highlighting their roles in supporting business operations and growth.',
    exampleOne:
      'Our primary revenue streams include direct product line sales, subscription-based services, and strategic partnerships for co-branded offerings. We forecast revenue growth based on market trends, assuming a steady increase in customer acquisition. Cost assumptions include stable production costs and incremental marketing expenses. Our key partners include suppliers for raw materials, distributors for market reach, and technology providers for platform support. Collaboration with these partners strengthens our value chain and expands our market presence.',
    exampleTwo:
      'Our core revenue sources encompass product sales through e-commerce platforms, licensing fees for proprietary software, and consultancy services tailored to client needs. Revenue projections hinge on market demand elasticity, with conservative estimates factoring in seasonal fluctuations. Cost assumptions prioritize operational efficiency and scalability, anticipating controlled expenditure growth. Strategic alliances with manufacturers ensure a stable supply chain, while digital marketing agencies drive brand visibility.',
  },
  [GLOBAL_FIELD_NAMES.MARKETING_STRATEGY]: {
    name: 'Sales strategy',
    instruction:
      'Describe the key methods you will use to attract and retain customers, such as advertising channels, pricing strategies, and promotional activities. Additionally, include your sales approach, detailing your sales channels, target markets, and sales targets.',
    exampleOne: ol([
      'Dealer and Distributor Network: The company partners with dealerships and distributors worldwide to sell its tracked tractors.',
      'Direct Sales: The company maintains a direct sales team responsible for engaging with customers, providing product information, and facilitating sales transactions.',
    ]),
    exampleTwo:
      'The  company sells its services through various channels, including the online platform, direct sales teams, partnerships with language service providers, affiliate programs, and participation in industry events and conferences. Additionally, the company utilizes digital marketing strategies such as social media, email campaigns.',
  },
};
